import { getFrontEndName } from "@/utils/productName.utils";

// 兌換券選用時自動加入的購物車品項，帶 couponExchange: { memberCouponUuid } 標記。

const getList = (value) => (Array.isArray(value) ? value : []);
const isComboMenuItem = (item) => item?.comboSubSettings !== undefined;

export const isCouponExchangeItem = (item) =>
  !!item?.couponExchange?.memberCouponUuid;

export const getCouponExchangeItemNames = (items) =>
  [
    ...new Set(
      getList(items)
        .map((item) => getFrontEndName(item.product?.name))
        .filter(Boolean)
    ),
  ].join("、");

// 移除 keepMemberCouponUuid 以外的兌換行；傳 null 移除全部兌換行。
export function removeCouponExchangeItems(cart, keepMemberCouponUuid) {
  return getList(cart).filter(
    (item) =>
      !isCouponExchangeItem(item) ||
      (keepMemberCouponUuid &&
        item.couponExchange.memberCouponUuid === keepMemberCouponUuid)
  );
}

// 購物車中某商品的數量（含套餐子項），與庫存檢查口徑一致。
export function getProductQuantityInCart(cart, productId) {
  let quantity = 0;
  const addProductCount = (product, count = 1) => {
    if (product?.id === productId) quantity += Number(count) || 0;
  };
  for (const orderItem of getList(cart)) {
    const orderQuantity = Number(orderItem.quantity) || 1;
    if (!orderItem.isCombo) {
      addProductCount(orderItem.product, orderQuantity);
      continue;
    }
    getList(orderItem.fixedProducts).forEach((product) =>
      addProductCount(product, orderQuantity)
    );
    getList(orderItem.comboSubItem).forEach((group) => {
      getList(group.products)
        .concat(getList(group.markUpProducts))
        .forEach((product, index) => {
          addProductCount(
            product,
            (group.productSelectCount?.[index] || 0) * orderQuantity
          );
        });
    });
    getList(orderItem.comboSubAnyProducts?.orderProducts).forEach((product) =>
      addProductCount(product, orderQuantity)
    );
  }
  return quantity;
}

// 商品規格初始化（各選項數量為 0）；回傳新物件，不改動菜單資料。
export function getInitialProductSpecSettings(product, specifications = []) {
  return getList(product?.productSpecSettings)
    .map((subSpec) => {
      const subSpecID = String(subSpec.subSpecID || "").trim();
      const spec = getList(specifications).find(
        (item) => String(item.id || "").trim() === subSpecID
      );
      if (!spec) return { ...subSpec };
      return {
        ...subSpec,
        subSpecOptionSelectCount: spec.specOptions?.map(() => 0) || [],
        ...spec,
      };
    })
    .filter((spec) => spec.enableForProductComboSpec === true);
}

// 非 DB 菜單或非庫存模式不限量；售完為 0。
function getStockRemaining(menuItem, cart, useDbData) {
  if (!useDbData || Number(menuItem.stockMode) !== 1) return Infinity;
  if (menuItem.isStockSoldOut) return 0;
  return Math.max(
    Number(menuItem.stockCount || 0) -
      getProductQuantityInCart(cart, menuItem.id),
    0
  );
}

/**
 * 判斷兌換券的兌換商品如何處理：
 * ready：另加一行兌換行（購物車已有會員自點的同商品也一樣）
 * use_existing：庫存不夠再加，改兌換購物車既有品項
 * out_of_stock：庫存不夠再加，既有數量也不足
 * unavailable：時段、取餐方式等非庫存原因不能點，或菜單上找不到
 */
export function getCouponExchangeTarget({
  rules,
  cart = [],
  menuGroups = [],
  isCategoryVisible = () => true,
  useDbData = false,
  specifications = [],
}) {
  if (rules?.benefit_type !== "target_exchange")
    return { status: "not_exchange" };
  const target = rules.benefit_targets?.[0];
  const quantity = rules.benefit_quantity;
  if (
    !["product", "combo"].includes(target?.target_type) ||
    !Number.isSafeInteger(quantity) ||
    quantity < 1
  ) {
    return { status: "not_exchange" };
  }
  const isCombo = target.target_type === "combo";
  const isTarget = (item) =>
    item?.id === target.target_id && isComboMenuItem(item) === isCombo;
  const existingQuantity = getList(cart)
    .filter(
      (item) =>
        !isCouponExchangeItem(item) &&
        !!item.isCombo === isCombo &&
        item.product?.id === target.target_id
    )
    .reduce((total, item) => total + (Number(item.quantity) || 0), 0);

  // 與點餐頁一致：所在分類於目前時段可見，且本取餐方式有販售
  const visibleItems = getList(menuGroups)
    .filter((group) => isCategoryVisible(group.categoryId))
    .flatMap((group) => getList(group.products));
  const menuItem = visibleItems.find(
    (item) =>
      isTarget(item) && item.itemType !== "retailGroup" && item.availableInStore
  );
  if (!menuItem) return { status: "unavailable" };

  if (getStockRemaining(menuItem, cart, useDbData) < quantity) {
    return existingQuantity >= quantity
      ? { status: "use_existing", menuItem, quantity }
      : { status: "out_of_stock", menuItem, quantity };
  }
  const needsDetail =
    isCombo ||
    getInitialProductSpecSettings(menuItem, specifications).length > 0;
  return { status: "ready", menuItem, quantity, needsDetail };
}

// 試算用的兌換行（未選規格）；需要開詳情的商品實際加入時以詳情結果為準。
export function getCouponExchangePreviewItem({
  menuItem,
  quantity,
  memberCouponUuid,
}) {
  const isCombo = isComboMenuItem(menuItem);
  return {
    product: menuItem,
    quantity,
    isCombo,
    settings: [],
    ...(isCombo
      ? { fixedProducts: [], comboSubItem: [], comboSubAnyProducts: {} }
      : {}),
    note: "",
    couponExchange: { memberCouponUuid },
  };
}

// 不需開詳情（單品無規格）時直接加入購物車的品項。
export function buildCouponExchangeOrderItem({
  menuItem,
  quantity,
  specifications,
  memberCouponUuid,
}) {
  return {
    product: menuItem,
    quantity,
    isCombo: false,
    settings: getInitialProductSpecSettings(menuItem, specifications),
    note: "",
    couponExchange: { memberCouponUuid },
  };
}
